import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import RFB from '../features/remoteVnc/vendor/novnc/core/rfb.js';
import KeyTable from '../features/remoteVnc/vendor/novnc/core/input/keysym.js';
import { buildRemoteVncWebSocketUrl } from '../features/remoteVnc/utils/buildRemoteVncWebSocketUrl';
import './RemoteVnc.css';

const REPLACED_VIEWER_CLOSE_CODE = 4001;
const STALE_VIEWER_CLOSE_CODE = 4002;

const createViewerSessionMetadata = () => ({
  viewerInstanceId:
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `viewer-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  pageLoadedAtMs: Date.now(),
});

type RfbInstance = {
  disconnect: () => void;
  addEventListener: (event: string, listener: (event: any) => void) => void;
  sendKey: (keysym: number, code?: string) => void;
  viewOnly: boolean;
  scaleViewport: boolean;
  resizeSession: boolean;
  clipViewport: boolean;
  background: string;
  showDotCursor: boolean;
};

const INITIAL_CONNECT_TIMEOUT_MS = 20000;

type RemoteVncProps = {
  deviceIdOverride?: string | null;
  showBackButton?: boolean;
};

const RemoteVnc: React.FC<RemoteVncProps> = ({ deviceIdOverride, showBackButton = true }) => {
  const { deviceId: routeDeviceId = '' } = useParams();
  const deviceId = (deviceIdOverride ?? routeDeviceId).trim();
  const screenRef = useRef<HTMLDivElement | null>(null);
  const rfbRef = useRef<RfbInstance | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const handshakeTimerRef = useRef<number | null>(null);
  const connectedRef = useRef(false);
  const handshakeTimedOutRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const viewerSessionRef = useRef(createViewerSessionMetadata());
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [overlayMessage, setOverlayMessage] = useState('Starting remote screen...');

  useEffect(() => {
    const screen = screenRef.current;
    if (!screen || !deviceId) {
      return undefined;
    }

    const sendBack = () => {
      if (!rfbRef.current) {
        return;
      }

      rfbRef.current.sendKey((KeyTable as Record<string, number>).XK_Escape, 'Escape');
    };

    const clearHandshakeTimer = () => {
      if (handshakeTimerRef.current !== null) {
        window.clearTimeout(handshakeTimerRef.current);
        handshakeTimerRef.current = null;
      }
    };

    const scheduleReconnect = (delayMs: number) => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
      }

      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delayMs);
    };

    const armHandshakeTimeout = (rfb: RfbInstance) => {
      clearHandshakeTimer();
      handshakeTimerRef.current = window.setTimeout(() => {
        if (rfbRef.current !== rfb || connectedRef.current) {
          return;
        }

        handshakeTimerRef.current = null;
        handshakeTimedOutRef.current = true;
        reconnectAttemptsRef.current += 1;
        setOverlayMessage('Device did not respond. Retrying...');
        setStatusMessage('Timed out waiting for device. Retrying...');

        try {
          rfb.disconnect();
        } catch (_error) {
          handshakeTimedOutRef.current = false;
          scheduleReconnect(Math.min(2500 + reconnectAttemptsRef.current * 500, 6000));
        }
      }, INITIAL_CONNECT_TIMEOUT_MS);
    };

    const connect = () => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      clearHandshakeTimer();
      connectedRef.current = false;
      handshakeTimedOutRef.current = false;

      if (rfbRef.current) {
        try {
          rfbRef.current.disconnect();
        } catch (_error) {
          // Ignore disconnect errors during reconnect cleanup.
        }
        rfbRef.current = null;
      }

      setOverlayMessage(
        reconnectAttemptsRef.current === 0
          ? 'Starting remote screen...'
          : 'Reconnecting to device...',
      );
      setStatusMessage(null);

      const rfb = new (RFB as any)(
        screen,
        buildRemoteVncWebSocketUrl(deviceId, undefined, undefined, viewerSessionRef.current),
        {
          credentials: {},
        },
      ) as RfbInstance;
      armHandshakeTimeout(rfb);

      rfb.viewOnly = false;
      rfb.scaleViewport = true;
      rfb.resizeSession = false;
      rfb.clipViewport = false;
      rfb.background = 'rgb(0, 0, 0)';
      rfb.showDotCursor = true;

      rfb.addEventListener('connect', () => {
        clearHandshakeTimer();
        connectedRef.current = true;
        reconnectAttemptsRef.current = 0;
        setOverlayMessage('');
        setStatusMessage(null);
      });

      rfb.addEventListener('disconnect', (event: any) => {
        clearHandshakeTimer();
        connectedRef.current = false;

        const closeCode = Number(event?.detail?.code);
        if (closeCode === REPLACED_VIEWER_CLOSE_CODE || closeCode === STALE_VIEWER_CLOSE_CODE) {
          const message =
            closeCode === REPLACED_VIEWER_CLOSE_CODE
              ? 'A newer Remote VNC page took control. Reload this page to reconnect.'
              : 'This page is no longer the newest viewer. Reload to reconnect.';
          setOverlayMessage('Session ended');
          setStatusMessage(message);
          return;
        }

        if (handshakeTimedOutRef.current) {
          handshakeTimedOutRef.current = false;
          scheduleReconnect(Math.min(2500 + reconnectAttemptsRef.current * 500, 6000));
          return;
        }

        const clean = Boolean(event?.detail?.clean);
        reconnectAttemptsRef.current += 1;
        setOverlayMessage(clean ? 'Connection closed. Reconnecting...' : 'Connection lost. Reconnecting...');
        setStatusMessage('Remote session disconnected. Reconnecting...');
        scheduleReconnect(clean ? 1500 : Math.min(2500 + reconnectAttemptsRef.current * 500, 6000));
      });

      rfb.addEventListener('securityfailure', (event: any) => {
        clearHandshakeTimer();
        connectedRef.current = false;
        const reason = event?.detail?.reason || 'Security negotiation failed';
        setOverlayMessage('Connection failed');
        setStatusMessage(reason);
      });

      rfb.addEventListener('credentialsrequired', () => {
        setStatusMessage('Password required for this session');
      });

      rfbRef.current = rfb;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        sendBack();
        event.preventDefault();
      }
    };

    const handleContextMenu = (event: Event) => {
      event.preventDefault();
      sendBack();
    };

    window.addEventListener('keydown', handleKeyDown);
    screen.addEventListener('contextmenu', handleContextMenu);
    connect();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      screen.removeEventListener('contextmenu', handleContextMenu);

      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
      }
      clearHandshakeTimer();

      if (rfbRef.current) {
        try {
          rfbRef.current.disconnect();
        } catch (_error) {
          // Ignore disconnect cleanup errors on unmount.
        }
        rfbRef.current = null;
      }
    };
  }, [deviceId]);

  if (!deviceId) {
    return (
      <div className="remote-vnc-page remote-vnc-page--empty">
        <div className="remote-vnc-overlay-card">
          <div className="remote-vnc-overlay-title">Missing device</div>
          <div className="remote-vnc-overlay-copy">A device id is required to open Remote VNC.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="remote-vnc-page">
      {statusMessage ? <div className="remote-vnc-status">{statusMessage}</div> : null}
      <div className="remote-vnc-shell">
        {showBackButton ? (
          <button
            className="remote-vnc-back-button"
            type="button"
            aria-label="Back"
            onClick={() => {
              if (rfbRef.current) {
                rfbRef.current.sendKey((KeyTable as Record<string, number>).XK_Escape, 'Escape');
              }
            }}
          >
            <span className="remote-vnc-back-arrow" aria-hidden="true" />
          </button>
        ) : null}
        <div className="remote-vnc-screen-wrap">
          <div ref={screenRef} className="remote-vnc-screen" />
          {overlayMessage ? (
            <div className="remote-vnc-overlay">
              <div className="remote-vnc-overlay-card">
                <div className="remote-vnc-overlay-title">Connecting to device</div>
                <div className="remote-vnc-overlay-copy">{overlayMessage}</div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RemoteVnc;
